import React, {
  memo,
  useEffect,
  useState,
} from "react";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

function cleanCoverTitle(value) {
  return (
    String(value || "Your memories")
      .replace(
        /[\p{Extended_Pictographic}\uFE0F\u200D]/gu,
        ""
      )
      .replace(/\s+/g, " ")
      .trim() || "Your memories"
  );
}

function formatCoverDate(value) {
  const text = String(value || "").trim();

  const iso = text.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/
  );

  if (iso) {
    return `${iso[2]}.${iso[3]}.${iso[1].slice(-2)}`;
  }

  const numeric = text.match(
    /^(\d{1,2})[/.](\d{1,2})[/.](\d{4})$/
  );

  if (numeric) {
    const month = numeric[1].padStart(2, "0");
    const day = numeric[2].padStart(2, "0");
    const year = numeric[3].slice(-2);

    return `${month}.${day}.${year}`;
  }

  return text;
}

function CoverPhoto({ src, position }) {
  const [status, setStatus] = useState("loading");

  if (status === "error") return null;

  return (
    <Box
      component="img"
      src={src}
      alt=""
      width={320}
      height={320}
      decoding="async"
      loading="eager"
      draggable={false}
      onLoad={() => setStatus("ready")}
      onError={() => setStatus("error")}
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        objectFit: "cover",
        objectPosition: position,
        visibility:
          status === "ready" ? "visible" : "hidden",
        pointerEvents: "none",
      }}
    />
  );
}

function StorybookCover({
  imageUrl = "",
  eventTitle = "Your memories",
  eventDateLabel = "",
  loadPhoto = false,
  photoPosition = "center",
}) {
  const title = cleanCoverTitle(eventTitle);
  const date = formatCoverDate(eventDateLabel);

  useEffect(() => {
    if (!loadPhoto || typeof document === "undefined") {
      return;
    }

    const fontId = "storybook-pinyon-script-font";

    if (document.getElementById(fontId)) return;

    const link = document.createElement("link");
    link.id = fontId;
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Pinyon+Script&display=swap";

    document.head.appendChild(link);
  }, [loadPhoto]);

  const titleSize = Math.max(
    12,
    Math.min(25, 420 / Math.max(title.length, 1))
  );

  return (
    <Box
      sx={{
        display: "grid",
        placeItems: "center",
        py: 2,
      }}
    >
      <Box
        role="img"
        aria-label={`Sample photo book cover for ${title}`}
        sx={{
          position: "relative",
          width: {
            xs: 176,
            sm: 208,
          },
          aspectRatio: "1 / 1",
          transform: "rotate(-5deg)",

          // Thin, square-edged cover.
          borderRadius: 0,
          overflow: "hidden",
          bgcolor: "#30332f",

          // Small contact shadow, without a thick page stack.
          boxShadow:
            "0 1px 1px rgba(0,0,0,.20), 2px 4px 8px rgba(0,0,0,.12)",
        }}
      >
        {loadPhoto && imageUrl && (
          <CoverPhoto
            key={imageUrl}
            src={imageUrl}
            position={photoPosition}
          />
        )}

        {/* Subtle contrast behind the lettering */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, transparent 60%, rgba(0,0,0,.08) 73%, rgba(0,0,0,.48) 100%)",
            pointerEvents: "none",
          }}
        />

        {/* Centered title and date */}
        <Box
          sx={{
            position: "absolute",
            left: "7%",
            right: "7%",
            bottom: "6%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "3px",
            textAlign: "center",
            color: "#fff",
          }}
        >
          <Typography
            sx={{
              width: "100%",
              m: 0,
              fontFamily:
                '"Pinyon Script", "Snell Roundhand", "Edwardian Script ITC", cursive',
              fontSize: {
                xs: titleSize,
                sm: titleSize * 1.18,
              },
              fontWeight: 400,
              lineHeight: 1.12,
              letterSpacing: 0,
              color: "#fff",
              textWrap: "balance",
              overflowWrap: "anywhere",
              textShadow:
                "0 1px 3px rgba(0,0,0,.25)",
            }}
          >
            {title}
          </Typography>

          {date && (
            <Typography
              sx={{
                m: 0,
                fontFamily:
                  'Georgia, "Times New Roman", serif',
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: {
                  xs: 12,
                  sm: 14,
                },
                lineHeight: 1.1,
                letterSpacing: 0.3,
                color: "#fff",
                textShadow:
                  "0 1px 3px rgba(0,0,0,.25)",
              }}
            >
              {date}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export default memo(StorybookCover);